import { isMultiTenant } from "@/Configs/config";
import { DownloadIcon } from "@/Icons/v2/Download";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { ContaminationPageContext } from "../../context";
import { getContaminationRate } from "../../utils";
import { MAX_ROWS_COLLAPSED, TENANT_CONTAMINATION_COLUMNS } from "./constants";
import { TenantContaminationRowView } from "./tenantContaminationRow";
import { SortDir, SortKey, type TenantContaminationRow } from "./types";
import { exportExcel } from "./utils";

export const ContaminationByTenant = () => {
  const { t } = useTrans();
  const { timeFilter, selectedTenantCategoryIds } = useContext(
    ContaminationPageContext,
  );
  const { currentTerminal } = useTerminalsState();
  const { selectedSite } = useSelectedSite();

  const [sortKey, setSortKey] = useState<SortKey>(SortKey.CONTAMINATED_WEIGHT);
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.DESC);
  const [expanded, setExpanded] = useState(false);

  const onSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
        return;
      }
      setSortKey(key);
      setSortDir(SortDir.DESC);
    },
    [sortKey, sortDir],
  );

  const { data, isLoading } = trpc.dwh.customers.get.useQuery(
    {
      ...(timeFilter || defaultTimeFilter),
      terminalId: currentTerminal?.id || "",
      tenantCategories: selectedTenantCategoryIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "customers",
          currentTerminal?.id,
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
          selectedTenantCategoryIds,
        ].toString(),
      enabled: !!timeFilter && !!currentTerminal,
    },
  );

  const customersData: TenantContaminationRow[] = useMemo(
    () =>
      (data?.customers || [])
        .map((c) => ({
          id: c.customerId,
          name: c.customerName,
          nrLoads: c.totalContaminatedOccurrences || 0,
          contaminatedWeight: +(
            c.totalContaminatedWasteWeight?.quantity || 0
          ).toFixed(1),
          contaminationRate: getContaminationRate(c),
        }))
        .filter((d) => !!d.nrLoads),
    [data],
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...customersData].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue) * dir;
      }

      return ((aValue as number) - (bValue as number)) * dir;
    });
  }, [sortKey, sortDir, customersData]);

  const shownRows = useMemo(
    () => (expanded ? sortedRows : sortedRows.slice(0, MAX_ROWS_COLLAPSED)),
    [expanded, sortedRows],
  );

  const sitePath = useMemo(() => {
    let path = "";

    if (isMultiTenant && selectedSite?.siteName) {
      path = `/${selectedSite.siteName}`;
    }

    return path;
  }, [selectedSite]);

  const timeFilterUrlParam = useMemo(() => {
    if (!timeFilter) return "";

    return `${timeFilter.startTimeISO}&${timeFilter.endTimeISO}`;
  }, [timeFilter]);

  const exportContaminationByTenant = useCallback(async () => {
    await exportExcel({
      timeframe: timeFilter || defaultTimeFilter,
      rows: sortedRows,
    });
  }, [timeFilter, sortedRows]);

  return (
    <div className="flex h-full flex-col rounded-xl bg-white p-[24px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-dm-sans text-[18px] font-bold">
            {t("contaminationPage:tenantContaminationTitle")}
          </div>
          <div className="font-dm-sans text-grey6 text-[12.5px]">
            {t("contaminationPage:tenantContaminationSubtitle")}
          </div>
        </div>
        {!isLoading && (
          <button
            type="button"
            className="border-grey3 flex cursor-pointer items-center gap-3 rounded-lg border bg-white px-[14px] py-[8px] text-[13px] font-medium text-[#1D1C1C]"
            onClick={exportContaminationByTenant}
          >
            <DownloadIcon />
            {t("contaminationPage:tenantContaminationExportButton")}
          </button>
        )}
      </div>

      <ViewWithLoader isLoading={isLoading}>
        <div className="mt-[14px] overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr>
                {TENANT_CONTAMINATION_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => onSort(column.key)}
                    className={`text-grey6 cursor-pointer px-[12px] pb-[11px] text-[11px] font-semibold tracking-[0.04em] whitespace-nowrap uppercase select-none ${
                      column.align === "right" ? "text-right" : "text-left"
                    }`}
                  >
                    <span className="inline-flex items-center gap-[6px]">
                      {t(column.label)}
                      {sortKey === column.key && (
                        <span>{sortDir === "asc" ? "▲" : "▼"}</span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row) => (
                <TenantContaminationRowView
                  key={row.id}
                  row={row}
                  sitePath={sitePath}
                  timeFilterUrlParam={timeFilterUrlParam}
                />
              ))}
            </tbody>
          </table>
        </div>

        {sortedRows.length > MAX_ROWS_COLLAPSED && (
          <div className="mt-[14px] flex justify-end">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="border-grey3 flex cursor-pointer items-center gap-[6px] rounded-lg border bg-white px-[14px] py-[8px] text-[13px] font-semibold text-[#1D1C1C]"
            >
              {t(
                expanded
                  ? "contaminationPage:tenantContaminationShowLess"
                  : "contaminationPage:tenantContaminationSeeAllTenants",
                {
                  nrTenants: sortedRows.length,
                },
              )}
            </button>
          </div>
        )}
      </ViewWithLoader>
    </div>
  );
};
